import classNames from 'classnames'
import React from 'react'
import { isNullOrWhiteSpace } from '../utilities/string'

export interface Quote {
  text?: string
  name?: string
  role?: string
  imageUrl?: string
}

export interface QuoteListProps {
  additionalClassNames?: string

  disableGutter?: boolean

  /** One or more quotes to display (results in slideshow if more than 1) */
  quotes: Quote[]
}

export const renderQuoteList = ({
  additionalClassNames,
  disableGutter = false,
  quotes,
}: QuoteListProps): JSX.Element[] | null => {
  if (!quotes?.length) {
    return null
  }

  const wrapperClass = classNames(
    'flex flex-row flex-wrap justify-between items-start pr-20 text-secondary-dark sm:justify-start sm:pr-0',
    additionalClassNames
  )

  return quotes.map((quote, i) => {
    let className = wrapperClass

    if (disableGutter === false) {
      if (i !== 0) {
        className = classNames(className, 'pt-30 border-t border-neutral-lighter')
      }

      if (i < quotes.length - 1) {
        className = classNames(className, 'pb-30')
      }
    }

    return (
      <div key={i} className={className}>
        {!isNullOrWhiteSpace(quote.text) && (
          <blockquote
            className={classNames(
              'flex flex-col gap-15 flex-none order-3 w-full mt-15 pr-15 font-t-light text-t-400',
              'sm:w-auto sm:flex-1 sm:mt-0 sm:pr-30 sm:order-1',
              'md:mr-0',
              'lg:pr-45',
              'xl:pr-90',
              'xxl:pr-120'
            )}
          >
            {quote.text
              .split(/\r?\n/)
              .filter(text => !isNullOrWhiteSpace(text))
              .map((text, index) => (
                <div key={`text-${index}`}>"{text.trim()}"</div>
              ))}
          </blockquote>
        )}

        {quote.imageUrl && (
          <div
            className={classNames(
              'w-60 h-60 flex-shrink-0 overflow-hidden rounded-15 order-2',
              'sm:order-2',
              'md:w-120 md:h-120'
            )}
          >
            <img alt={quote.name} width="120" height="120" src={quote.imageUrl} />
          </div>
        )}

        {(quote.name || quote.role) && (
          <div className="flex-1 order-1 mt-15 pr-15 font-t text-t-100 sm:text-t-300 sm:flex-full sm:order-3 sm:w-full sm:pr-0 sm:mt-30">
            {quote.name && (
              <span>
                {quote.name}
                {quote.role && '.'}&nbsp;
              </span>
            )}

            {quote.role && (
              <span className={quote.name ? 'font-light' : ''}>{quote.role}</span>
            )}
          </div>
        )}
      </div>
    )
  })
}

// A JSX wrapper around renderQuoteList
// renderQuoteList itself returns an array which is needed for slideshows,
// but Typescript does not allow arrays to be used as JSX
export const QuoteList: React.FC<QuoteListProps> = props => <>{renderQuoteList(props)}</>
